<template>
  <div class="income-overview-container">
    <div class="page-header">
      <div>
        <h2>总收入</h2>
        <p>查看所有支付成功的充值收入、业务来源和到账明细</p>
      </div>
    </div>

    <div v-if="loading" class="loading-container">
      <el-icon class="is-loading">
        <Loading />
      </el-icon>
      <span>加载中...</span>
    </div>

    <div v-else-if="incomeData">
      <div class="statistics-cards income-stat-grid">
        <el-card class="stat-card income-primary">
          <div class="stat-content">
            <div class="stat-icon">
              <el-icon><Money /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-label">总实收</div>
              <div class="stat-value">¥{{ formatAmount(summary.totalAmount) }}</div>
            </div>
          </div>
        </el-card>

        <el-card class="stat-card success">
          <div class="stat-content">
            <div class="stat-icon">
              <el-icon><CircleCheck /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-label">成功订单</div>
              <div class="stat-value">{{ summary.orderCount }}</div>
            </div>
          </div>
        </el-card>

        <el-card class="stat-card users">
          <div class="stat-content">
            <div class="stat-icon">
              <el-icon><UserFilled /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-label">支付用户</div>
              <div class="stat-value">{{ summary.userCount }}</div>
            </div>
          </div>
        </el-card>

        <el-card class="stat-card amount">
          <div class="stat-content">
            <div class="stat-icon">
              <el-icon><Wallet /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-label">客单价</div>
              <div class="stat-value">¥{{ formatAmount(summary.averageOrderAmount) }}</div>
            </div>
          </div>
        </el-card>

        <el-card class="stat-card alipay">
          <div class="stat-content">
            <div class="stat-icon">
              <el-icon><DocumentCopy /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-label">支付宝实收</div>
              <div class="stat-value">¥{{ formatAmount(getChannelAmount('alipay')) }}</div>
            </div>
          </div>
        </el-card>

        <el-card class="stat-card wechat">
          <div class="stat-content">
            <div class="stat-icon">
              <el-icon><Refresh /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-label">微信实收</div>
              <div class="stat-value">¥{{ formatAmount(getChannelAmount('wechat')) }}</div>
            </div>
          </div>
        </el-card>
      </div>

      <div class="filter-section">
        <el-input
          v-model="keyword"
          placeholder="搜索订单号/用户/手机号"
          clearable
          style="width: 250px"
          @clear="handleSearch"
          @keyup.enter="handleSearch"
        >
          <template #prefix>
            <el-icon><Search /></el-icon>
          </template>
        </el-input>

        <el-select
          v-model="filterChannel"
          placeholder="支付渠道"
          clearable
          style="width: 160px"
          @change="handleSearch"
        >
          <el-option label="全部渠道" value="" />
          <el-option label="支付宝" value="alipay" />
          <el-option label="微信支付" value="wechat" />
        </el-select>

        <el-select
          v-model="filterBusinessType"
          placeholder="业务类型"
          clearable
          style="width: 180px"
          @change="handleBusinessTypeChange"
        >
          <el-option label="全部业务" value="" />
          <el-option label="AI 面试" value="ai_interview" />
          <el-option label="AI 优化简历" value="ai_resume_optimize" />
          <el-option label="旺旺币充值" value="wwcoin" />
          <el-option label="其他" value="other" />
        </el-select>

        <el-select
          v-model="filterPlanId"
          placeholder="套餐"
          clearable
          style="width: 180px"
          @change="handleSearch"
        >
          <el-option label="全部套餐" value="" />
          <el-option
            v-for="plan in currentPlanOptions"
            :key="plan.value"
            :label="plan.label"
            :value="plan.value"
          />
        </el-select>

        <el-date-picker
          v-model="dateRange"
          type="daterange"
          value-format="YYYY-MM-DD"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          style="width: 260px"
          @change="handleSearch"
        />

        <el-button type="primary" @click="handleSearch">
          <el-icon><Search /></el-icon>
          搜索
        </el-button>
      </div>

      <div class="insight-grid">
        <el-card class="chart-card income-chart-card">
          <template #header>
            <div class="card-header">
              <span class="card-title">收入趋势</span>
              <span class="card-subtitle">{{ chartDateText }}</span>
            </div>
          </template>
          <div
            v-show="chartDailyStats.length"
            ref="incomeChartRef"
            class="income-chart"
          />
          <el-empty
            v-if="!chartDailyStats.length"
            description="暂无趋势数据"
            :image-size="100"
          />
        </el-card>

        <el-card class="chart-card structure-card">
          <template #header>
            <div class="card-header">
              <span class="card-title">收入结构</span>
            </div>
          </template>
          <div v-if="businessTypeStats.length" class="structure-list">
            <div
              v-for="item in businessTypeStats"
              :key="item.businessType"
              class="structure-item"
            >
              <div class="structure-row">
                <span>{{ item.businessTypeName }}</span>
                <strong>¥{{ formatAmount(item.amount) }}</strong>
              </div>
              <div class="structure-meta">
                <span>{{ item.count }} 单</span>
                <span>{{ getBusinessPercent(item) }}%</span>
              </div>
              <div class="structure-bar">
                <span
                  :class="`bar-${item.businessType}`"
                  :style="{ width: `${getBusinessPercent(item)}%` }"
                />
              </div>
            </div>
          </div>
          <el-empty v-else description="暂无结构数据" :image-size="100" />
        </el-card>
      </div>

      <el-card class="table-card">
        <el-table
          :data="incomeRecords"
          stripe
          style="width: 100%"
          empty-text="暂无收入记录"
        >
          <el-table-column label="用户" min-width="220">
            <template #default="{ row }">
              <div class="user-info">
                <el-avatar :src="row.user?.avatar" :size="40" />
                <div class="user-details">
                  <div class="username">{{ getUserName(row) }}</div>
                  <div class="user-email">{{ getUserSubText(row) }}</div>
                </div>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="业务/套餐" min-width="190">
            <template #default="{ row }">
              <div class="plan-info">
                <el-tag
                  :type="getBusinessTypeTag(row.businessType)"
                  effect="dark"
                  size="small"
                >
                  {{ row.businessTypeName }}
                </el-tag>
                <div class="plan-name">{{ row.planName || '-' }}</div>
                <div class="plan-id">{{ row.planId || '-' }}</div>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="订单号" min-width="240">
            <template #default="{ row }">
              <div class="order-info">
                <div class="order-id">{{ row.orderId }}</div>
                <div class="order-id-small">ID: {{ row._id }}</div>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="支付信息" width="220">
            <template #default="{ row }">
              <div class="payment-info">
                <div class="paid-amount">¥{{ formatAmount(row.paidAmount) }}</div>
                <div class="payment-meta">
                  <el-tag :type="getChannelTag(row.channel)" size="small">
                    {{ row.channelName }}
                  </el-tag>
                  <span>{{ formatDate(row.paidAt) }}</span>
                </div>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="到账状态" width="140">
            <template #default="{ row }">
              <el-tag :type="getSettlementTag(row.settlementStatus)">
                {{ row.settlementStatusName }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column label="创建时间" width="180">
            <template #default="{ row }">
              {{ formatDate(row.createdAt) }}
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-container">
          <el-pagination
            v-model:current-page="page"
            v-model:page-size="limit"
            :page-sizes="[10, 20, 50, 100]"
            :total="incomeData.total"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSizeChange"
            @current-change="handlePageChange"
          />
        </div>
      </el-card>
    </div>

    <div v-else class="no-data">
      <el-empty description="暂无数据" />
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import * as echarts from 'echarts'
import { ElMessage } from 'element-plus'
import {
  CircleCheck,
  DocumentCopy,
  Loading,
  Money,
  Refresh,
  Search,
  UserFilled,
  Wallet
} from '@element-plus/icons-vue'
import { getIncomeRecords } from '@/api/user'

const incomeData = ref(null)
const loading = ref(true)
const page = ref(1)
const limit = ref(20)
const keyword = ref('')
const filterChannel = ref('')
const filterBusinessType = ref('')
const filterPlanId = ref('')
const dateRange = ref([])
const incomeChartRef = ref(null)

let incomeChart = null

const defaultSummary = {
  totalAmount: 0,
  orderCount: 0,
  userCount: 0,
  averageOrderAmount: 0,
  channelStats: [],
  businessTypeStats: []
}

const allPlanOptions = [
  { label: '自定义充值', value: 'custom', businessType: 'wwcoin' },
  { label: '单次包', value: 'single', businessType: 'ai_interview' },
  { label: '突击包', value: 'pro', businessType: 'ai_interview' },
  { label: '冲刺包', value: 'max', businessType: 'ai_interview' },
  { label: '上岸包', value: 'ultra', businessType: 'ai_interview' },
  {
    label: '1 元 1 次',
    value: 'resume_optimize_once',
    businessType: 'ai_resume_optimize'
  },
  {
    label: '5 元 10 次',
    value: 'resume_optimize_pack10',
    businessType: 'ai_resume_optimize'
  }
]

const summary = computed(() => incomeData.value?.summary || defaultSummary)

const incomeRecords = computed(() => incomeData.value?.data || [])

const chartDailyStats = computed(() => incomeData.value?.chart?.dailyStats || [])

const businessTypeStats = computed(() => summary.value.businessTypeStats || [])

const currentPlanOptions = computed(() => {
  if (!filterBusinessType.value || filterBusinessType.value === 'other') {
    return filterBusinessType.value === 'other'
      ? []
      : allPlanOptions
  }
  return allPlanOptions.filter(
    (plan) => plan.businessType === filterBusinessType.value
  )
})

const chartDateText = computed(() => {
  const start = incomeData.value?.chart?.startDate
  const end = incomeData.value?.chart?.endDate
  if (start && end) {
    return `${start} 至 ${end}`
  }
  if (start) {
    return `${start} 至今`
  }
  return '近 30 天'
})

const getIncomeRecordsData = async () => {
  try {
    loading.value = true
    const response = await getIncomeRecords({
      page: page.value,
      limit: limit.value,
      keyword: keyword.value,
      channel: filterChannel.value,
      businessType: filterBusinessType.value,
      planId: filterPlanId.value,
      startDate: dateRange.value?.[0] || '',
      endDate: dateRange.value?.[1] || ''
    })

    incomeData.value = {
      summary: response?.summary || defaultSummary,
      chart: response?.chart || { dailyStats: [] },
      total: response?.total || 0,
      page: response?.page || page.value,
      limit: response?.limit || limit.value,
      data: response?.data || []
    }
  } catch (error) {
    console.error('获取总收入数据失败:', error)
    ElMessage.error('获取总收入数据失败')
  } finally {
    loading.value = false
    setTimeout(() => initIncomeChart(), 0)
  }
}

const initIncomeChart = async () => {
  await nextTick()

  if (!incomeChartRef.value || !chartDailyStats.value.length) {
    if (incomeChart) {
      incomeChart.dispose()
      incomeChart = null
    }
    return
  }

  if (incomeChartRef.value.offsetWidth === 0) {
    setTimeout(() => initIncomeChart(), 180)
    return
  }

  if (!incomeChart) {
    incomeChart = echarts.init(incomeChartRef.value)
  }

  const dates = chartDailyStats.value.map((item) => item.date)
  const amounts = chartDailyStats.value.map((item) => item.amount || 0)
  const counts = chartDailyStats.value.map((item) => item.count || 0)

  incomeChart.setOption({
    color: ['#2563eb', '#16a34a'],
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross'
      },
      formatter(params) {
        const date = params?.[0]?.axisValue || ''
        const amountItem = params.find((item) => item.seriesName === '收入')
        const countItem = params.find((item) => item.seriesName === '订单数')
        return [
          date,
          `收入：¥${formatAmount(amountItem?.value || 0)}`,
          `订单数：${countItem?.value || 0} 单`
        ].join('<br/>')
      }
    },
    legend: {
      top: 0,
      right: 0,
      data: ['收入', '订单数']
    },
    grid: {
      top: 46,
      left: 8,
      right: 8,
      bottom: 8,
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: dates,
      axisTick: { alignWithLabel: true },
      axisLabel: {
        color: '#64748b'
      },
      axisLine: {
        lineStyle: { color: '#e5e7eb' }
      }
    },
    yAxis: [
      {
        type: 'value',
        name: '收入',
        axisLabel: {
          color: '#64748b',
          formatter: (value) => `¥${value}`
        },
        splitLine: {
          lineStyle: { color: '#eef2f7' }
        }
      },
      {
        type: 'value',
        name: '订单',
        axisLabel: {
          color: '#64748b'
        },
        splitLine: {
          show: false
        }
      }
    ],
    dataZoom: [
      {
        type: 'inside',
        start: 0,
        end: 100
      }
    ],
    series: [
      {
        name: '收入',
        type: 'bar',
        barMaxWidth: 28,
        data: amounts,
        itemStyle: {
          borderRadius: [4, 4, 0, 0],
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: '#3b82f6' },
            { offset: 1, color: '#1d4ed8' }
          ])
        }
      },
      {
        name: '订单数',
        type: 'line',
        yAxisIndex: 1,
        smooth: true,
        symbolSize: 7,
        data: counts,
        lineStyle: {
          width: 3,
          color: '#16a34a'
        },
        itemStyle: {
          color: '#16a34a'
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(22, 163, 74, 0.18)' },
            { offset: 1, color: 'rgba(22, 163, 74, 0.02)' }
          ])
        }
      }
    ]
  })
}

const getChannelAmount = (channel) => {
  const item = summary.value.channelStats?.find(
    (channelItem) => channelItem.channel === channel
  )
  return item?.amount || 0
}

const getBusinessPercent = (item) => {
  const total = businessTypeStats.value.reduce((sum, stat) => {
    return sum + Number(stat.amount || 0)
  }, 0)
  if (!total) return 0
  return Math.round((Number(item.amount || 0) / total) * 100)
}

const getUserName = (row) => {
  return (
    row.user?.wechatNickname ||
    row.user?.username ||
    row.user?.phone ||
    row.userId ||
    '未知用户'
  )
}

const getUserSubText = (row) => {
  return row.user?.email || row.user?.phone || row.user?._id || row.userId || '-'
}

const getChannelTag = (channel) => {
  const typeMap = {
    alipay: 'primary',
    wechat: 'success'
  }
  return typeMap[channel] || 'info'
}

const getBusinessTypeTag = (businessType) => {
  const typeMap = {
    ai_interview: 'primary',
    ai_resume_optimize: 'warning',
    wwcoin: 'success',
    other: 'info'
  }
  return typeMap[businessType] || 'info'
}

const getSettlementTag = (status) => {
  const typeMap = {
    settled: 'success',
    notification_only: 'primary',
    missing_actual_amount: 'warning'
  }
  return typeMap[status] || 'info'
}

const formatAmount = (amount) => {
  return Number(amount || 0).toFixed(2)
}

const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

const handleSearch = () => {
  page.value = 1
  getIncomeRecordsData()
}

const handleBusinessTypeChange = () => {
  filterPlanId.value = ''
  handleSearch()
}

const handleSizeChange = () => {
  page.value = 1
  getIncomeRecordsData()
}

const handlePageChange = () => {
  getIncomeRecordsData()
}

const handleResize = () => {
  if (incomeChart) {
    incomeChart.resize()
  }
}

onMounted(() => {
  window.addEventListener('resize', handleResize)
  getIncomeRecordsData()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
  if (incomeChart) {
    incomeChart.dispose()
  }
})
</script>

<style scoped lang="scss">
.income-overview-container {
  padding: 0;
  background-color: transparent;
  min-height: 100%;

  .income-stat-grid {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }

  .stat-card {
    .stat-value {
      font-size: 24px;
      white-space: nowrap;
    }

    &.income-primary .stat-icon {
      color: #0f766e !important;
      background: #ecfdf5 !important;
    }

    &.alipay .stat-icon {
      color: #2563eb !important;
      background: #eff6ff !important;
    }

    &.wechat .stat-icon {
      color: #16a34a !important;
      background: #ecfdf3 !important;
    }
  }

  .insight-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 320px;
    gap: 16px;
    margin-bottom: 16px;
  }

  .card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-width: 0;

    .card-title {
      color: var(--admin-color-text);
      font-size: 15px;
      font-weight: 700;
    }

    .card-subtitle {
      color: var(--admin-color-text-muted);
      font-size: 12px;
      white-space: nowrap;
    }
  }

  .income-chart {
    width: 100%;
    height: 320px;
  }

  .structure-list {
    display: flex;
    flex-direction: column;
    gap: 18px;
    min-height: 320px;
  }

  .structure-item {
    .structure-row,
    .structure-meta {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
    }

    .structure-row {
      color: var(--admin-color-text);
      font-size: 14px;
      font-weight: 600;

      strong {
        font-variant-numeric: tabular-nums;
      }
    }

    .structure-meta {
      margin-top: 4px;
      color: var(--admin-color-text-muted);
      font-size: 12px;
    }

    .structure-bar {
      position: relative;
      height: 8px;
      margin-top: 8px;
      overflow: hidden;
      border-radius: 999px;
      background: #eef2f7;

      span {
        display: block;
        height: 100%;
        min-width: 4px;
        border-radius: inherit;
        background: #64748b;
      }

      .bar-ai_interview {
        background: #2563eb;
      }

      .bar-ai_resume_optimize {
        background: #d97706;
      }

      .bar-wwcoin {
        background: #16a34a;
      }
    }
  }

  .user-info {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;

    .user-details {
      min-width: 0;
    }

    .username {
      color: var(--admin-color-text);
      font-size: 14px;
      font-weight: 600;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .user-email {
      margin-top: 3px;
      color: var(--admin-color-text-muted);
      font-size: 12px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .plan-info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;

    .plan-name {
      color: var(--admin-color-text);
      font-size: 13px;
      font-weight: 600;
    }

    .plan-id {
      color: var(--admin-color-text-muted);
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
        'Liberation Mono', monospace;
      font-size: 12px;
      word-break: break-all;
    }
  }

  .order-info {
    .order-id {
      color: var(--admin-color-text);
      font-size: 13px;
      font-weight: 600;
      word-break: break-all;
    }

    .order-id-small {
      margin-top: 4px;
      color: var(--admin-color-text-muted);
      font-size: 12px;
      word-break: break-all;
    }
  }

  .payment-info {
    .paid-amount {
      color: var(--admin-color-success);
      font-size: 18px;
      font-weight: 700;
      line-height: 24px;
    }

    .payment-meta {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-top: 6px;
      color: var(--admin-color-text-muted);
      font-size: 12px;
    }
  }
}

@media (max-width: 1080px) {
  .income-overview-container {
    .insight-grid {
      grid-template-columns: 1fr;
    }
  }
}

@media (max-width: 768px) {
  .income-overview-container {
    .stat-card .stat-value {
      white-space: normal;
    }

    .card-header {
      align-items: flex-start;
      flex-direction: column;
    }

    .income-chart {
      height: 280px;
    }
  }
}
</style>
